import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PublicLayout from '../components/PublicLayout';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICES = [
  {
    name: 'Operational Waste Management Plans',
    abbr: 'OpWMP',
    desc: 'Ongoing waste management strategies for residential, commercial, and mixed-use developments. Council-compliant documentation for DA submissions.',
    color: '#3b82f6', bgColor: '#eff6ff', borderColor: '#bfdbfe',
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  },
  {
    name: 'Demolition & Construction WMP',
    abbr: 'D&CWMP',
    desc: 'Demolition waste estimates, material recovery targets, and disposal logistics for construction projects of all scales.',
    color: '#f59e0b', bgColor: '#fffbeb', borderColor: '#fde68a',
    icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
  },
  {
    name: 'Construction Management Plans',
    abbr: 'CMP',
    desc: 'Site-specific construction management documentation covering waste handling, traffic management, and environmental controls.',
    color: '#10b981', bgColor: '#ecfdf5', borderColor: '#a7f3d0',
    icon: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064',
  },
  {
    name: 'Stormwater Management Plans',
    abbr: 'SWMP',
    desc: 'Stormwater quality and quantity management strategies, WSUD responses, and erosion and sediment control plans for development approvals.',
    color: '#6366f1', bgColor: '#eef2ff', borderColor: '#c7d2fe',
    icon: 'M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z',
  },
];

const STATS = [
  { value: '8+', label: 'Years Experience' },
  { value: '99+', label: 'WMPs Delivered' },
  { value: '64', label: 'Councils Covered' },
  { value: 'NSW + VIC', label: 'Service Areas' },
];

export default function Home() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) navigate('/dashboard');
    else setError(result.error);
  };

  return (
    <PublicLayout>
      <SEO
        title="Waste Management Plan Consultants Sydney & Melbourne"
        description="Specialist waste management plan consultants for Australian property developments. Operational WMPs, D&CWMPs, stormwater plans, and HAZMAT surveys for NSW & VIC councils. 8+ years experience."
        keywords="waste management plan consultant, waste management plan Sydney, waste management plan Melbourne, WMP consultant NSW, WMP consultant VIC, operational waste management plan, demolition waste management plan, stormwater management plan, HAZMAT survey, Green Star waste credit"
        canonicalPath="/"
      />

      {/* Hero */}
      <section className="relative py-20 lg:py-28 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-10 left-[15%] w-[350px] h-[350px] bg-blue-200/40 rounded-full blur-[120px]" />
          <div className="absolute bottom-10 right-[20%] w-[300px] h-[300px] bg-indigo-200/35 rounded-full blur-[100px]" />
          <div className="absolute top-[40%] left-[50%] w-[200px] h-[200px] bg-sky-100/30 rounded-full blur-[80px]" />
        </div>
        <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_1b929e83-aedd-4ae0-bcb2-1158c1580b12/artifacts/voiez9en_Vector%20Frame.jpg)',
            backgroundSize: 'auto 120%',
            backgroundPosition: 'right -5% top -10%',
            backgroundRepeat: 'no-repeat',
            maskImage: 'linear-gradient(to right, transparent 5%, rgba(0,0,0,0.12) 30%, rgba(0,0,0,0.35) 70%, rgba(0,0,0,0.6) 100%)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 5%, rgba(0,0,0,0.12) 30%, rgba(0,0,0,0.35) 70%, rgba(0,0,0,0.6) 100%)',
            opacity: 0.35,
          }} />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.4fr_0.6fr] gap-12 items-start">
            {/* Left — Consultant copy */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold mb-5 tracking-wide">
                WASTE MANAGEMENT PLAN CONSULTANTS
              </div>
              <h1 className="text-3xl lg:text-5xl font-black tracking-tighter leading-[1.1] mb-5" data-testid="hero-headline">
                <span className="text-[#0A2540]">Expert Waste Management Plans</span><br />
                <span className="text-[#0A2540]">for </span>
                <span className="bg-gradient-to-r from-blue-600 to-indigo-500 bg-clip-text text-transparent">NSW &amp; VIC</span>
                <span className="text-[#0A2540]"> Developments</span>
              </h1>
              <p className="text-base lg:text-lg text-slate-600 max-w-xl mb-8 leading-relaxed">
                We prepare professional, council-compliant Waste Management Plans for residential, commercial, and mixed-use developments across Sydney and Melbourne. From DA submissions to Green Star credits &mdash; delivered by experienced consultants, not automated software.
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <Link to="/contact" className="px-7 py-3 rounded-full bg-[#0A2540] text-white font-medium text-sm hover:bg-black transition-colors shadow-lg shadow-slate-900/10" data-testid="hero-cta-quote">
                  Request a Quote
                </Link>
                <Link to="/services" className="px-7 py-3 rounded-full border border-slate-200 text-[#0A2540] font-medium text-sm hover:bg-slate-50 transition-colors" data-testid="hero-cta-services">
                  View Services
                </Link>
                <Link to="/projects" className="px-7 py-3 text-blue-600 font-medium text-sm hover:text-blue-700 transition-colors" data-testid="hero-cta-projects">
                  See Our Work &rarr;
                </Link>
              </div>
            </div>

            {/* Right — Compact Login */}
            <div className="w-full max-w-[260px] mx-auto lg:mx-0 lg:ml-auto lg:mt-8">
              <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-[0_4px_30px_rgb(0,0,0,0.06)] border border-white/60 p-5" data-testid="compact-login-card">
                <div className="text-center mb-3">
                  <h2 className="text-sm font-bold text-[#0A2540]">Client Login</h2>
                  <p className="text-slate-400 text-[10px] mt-0.5">Access your projects</p>
                </div>
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-2 py-1 rounded-lg text-[10px] mb-2">{error}</div>
                )}
                <form onSubmit={handleLogin} className="space-y-2.5">
                  <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-slate-50/80 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all placeholder:text-slate-300"
                    placeholder="Email" data-testid="home-login-email" />
                  <input type="password" required value={password} onChange={e => setPassword(e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-slate-50/80 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all placeholder:text-slate-300"
                    placeholder="Password" data-testid="home-login-password" />
                  <button type="submit" disabled={loading}
                    className="w-full py-2 rounded-full bg-[#0A2540] text-white text-xs font-semibold hover:bg-black transition-colors disabled:opacity-50"
                    data-testid="home-login-submit">
                    {loading ? 'Signing in...' : 'Sign In'}
                  </button>
                </form>
                <div className="text-center mt-2 space-y-1">
                  <Link to="/forgot-password" className="block text-[10px] text-blue-600 hover:text-blue-700">Forgot password?</Link>
                  <Link to="/register" className="block text-[10px] text-slate-400 hover:text-slate-600">Create account</Link>
                </div>
              </div>
            </div>
          </div>

          {/* 4 Service Cards — full width, landscape, glassmorphism */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {SERVICES.map((s, i) => (
              <Link to="/services" key={i} className="group bg-white/50 backdrop-blur-md rounded-xl border px-5 py-4 shadow-[0_2px_12px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.1)] hover:bg-white/70 hover:-translate-y-0.5 transition-all duration-300" style={{ borderColor: s.borderColor }} data-testid={`service-card-${i}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: s.bgColor }}>
                    <svg className="w-[22px] h-[22px]" style={{ color: s.color }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={s.icon} />
                    </svg>
                  </div>
                  <h3 className="text-base font-bold text-[#0A2540] group-hover:text-blue-700 transition-colors leading-tight">{s.name}</h3>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">{s.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-blue-100/60 bg-gradient-to-r from-blue-50/40 via-white to-blue-50/40" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl lg:text-3xl font-black text-[#0A2540] tracking-tight">{s.value}</div>
                <div className="text-xs text-slate-400 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why PlanGen */}
      <section className="py-16 relative overflow-hidden" data-testid="why-section">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F0F4FF] via-[#F8FAFC] to-[#EEF2FF] -z-10" />
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-blue-100/25 rounded-full blur-[120px] -z-10" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">WHY PLANGEN CONSULTING</div>
            <h2 className="text-2xl lg:text-3xl font-bold tracking-tight text-[#0A2540] leading-tight">Specialist waste management planning for complex developments.</h2>
            <p className="text-base text-slate-500 mt-4 leading-relaxed">With over 8 years of experience delivering waste management plans for hospitals, universities, theatres, residential towers, and government projects &mdash; we bring specialist knowledge that generic environmental consultancies cannot match.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'Council-Specific Expertise', desc: 'We know the specific requirements, DCP controls, and submission formats for 64 councils across NSW and VIC. No guesswork, no generic templates.', icon: 'M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9' },
              { title: 'Green Star & SSDA Experience', desc: 'From Credit 7A/8A specialist plans to State Significant Development Applications, we handle projects that require deep regulatory knowledge.', icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z' },
              { title: 'Fast Turnaround', desc: 'Most Operational WMPs delivered within 5 business days. Complex projects with dedicated timelines and milestone tracking throughout.', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl border border-blue-100/60 p-7 shadow-[0_2px_12px_rgb(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.07)] transition-all duration-300" data-testid={`why-card-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center mb-4">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.icon} />
                  </svg>
                </div>
                <h3 className="text-base font-bold text-[#0A2540] mb-2">{item.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-16 bg-white" data-testid="featured-projects-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">SELECTED PROJECTS</div>
              <h2 className="text-2xl lg:text-3xl font-bold tracking-tight text-[#0A2540]">Recent work</h2>
            </div>
            <Link to="/projects" className="hidden md:inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors" data-testid="view-all-projects">
              View all projects <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Whitlam Library */}
            <Link to="/projects" className="group bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-[0_2px_12px_rgb(0,0,0,0.03)] hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)] transition-all duration-300" data-testid="project-whitlam">
              <div className="h-48 bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                <div className="text-center px-8">
                  <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-3">
                    <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
                  </div>
                  <p className="text-sm font-bold text-blue-900">Whitlam Library</p>
                  <p className="text-xs text-blue-600 mt-1">Western Sydney University</p>
                </div>
              </div>
              <div className="p-6">
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">Green Star</span>
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">City of Parramatta</span>
                </div>
                <h3 className="text-lg font-bold text-[#0A2540] mb-2 group-hover:text-blue-700 transition-colors">Whitlam Library Upgrade</h3>
                <p className="text-sm text-slate-500 leading-relaxed">Green Star Interiors v1.3 Credit 7A Operational Waste Management Plan for the Parramatta South campus library fitout.</p>
              </div>
            </Link>

            {/* Foundation Theatres */}
            <Link to="/projects" className="group bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-[0_2px_12px_rgb(0,0,0,0.03)] hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)] transition-all duration-300" data-testid="project-foundation">
              <div className="h-48 bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center">
                <div className="text-center px-8">
                  <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                    <svg className="w-7 h-7 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                  </div>
                  <p className="text-sm font-bold text-amber-900">Foundation Theatres</p>
                  <p className="text-xs text-amber-600 mt-1">The Star, Pyrmont</p>
                </div>
              </div>
              <div className="p-6">
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">D&amp;CWMP</span>
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Pyrmont, Sydney</span>
                </div>
                <h3 className="text-lg font-bold text-[#0A2540] mb-2 group-hover:text-blue-700 transition-colors">Foundation Theatres Cold Shell Redevelopment</h3>
                <p className="text-sm text-slate-500 leading-relaxed">Demolition &amp; Construction Waste Management Plan for a 431-seat theatre redevelopment at The Star entertainment precinct.</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Application Types */}
      <section className="py-16 bg-gradient-to-b from-[#F8FAFC] to-[#EFF4FF]" data-testid="applications-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">APPLICATION TYPES</div>
            <h2 className="text-2xl lg:text-3xl font-bold tracking-tight text-[#0A2540]">We prepare WMPs for all submission types</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-4xl mx-auto">
            {[
              { name: 'Development Application', abbr: 'DA' },
              { name: 'Construction Certificate', abbr: 'CC' },
              { name: 'Complying Development', abbr: 'CDC' },
              { name: 'State Significant Development', abbr: 'SSDA' },
              { name: 'Green Star Credits', abbr: '7A / 8A / 22B' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl border border-blue-100/60 p-5 text-center shadow-[0_2px_8px_rgb(0,0,0,0.02)] hover:shadow-[0_4px_16px_rgb(59,130,246,0.1)] hover:border-blue-200 transition-all" data-testid={`app-type-${i}`}>
                <div className="text-lg font-black text-[#0A2540]">{item.abbr}</div>
                <p className="text-[10px] text-slate-500 mt-1 leading-tight">{item.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Areas We Serve */}
      <section className="py-16 bg-white" data-testid="areas-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">AREAS WE SERVE</div>
            <h2 className="text-2xl lg:text-3xl font-bold tracking-tight text-[#0A2540]">Waste Management Plan consultants across NSW &amp; VIC</h2>
            <p className="text-sm text-slate-500 mt-3 max-w-2xl mx-auto">We prepare council-compliant WMPs for developments in all local government areas across New South Wales and Victoria. Below are some of our key service areas.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div>
              <h3 className="text-base font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded bg-blue-50 flex items-center justify-center text-blue-600 text-xs font-black">N</span>
                New South Wales
              </h3>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                {[
                  { name: 'City of Sydney', slug: 'city-of-sydney' },
                  { name: 'Canterbury-Bankstown', slug: 'canterbury-bankstown' },
                  { name: 'Waverley', slug: 'waverley' },
                  { name: 'Blacktown', slug: 'blacktown' },
                  { name: 'The Hills Shire', slug: 'the-hills' },
                  { name: 'Central Coast', slug: 'central-coast' },
                  { name: 'Parramatta', slug: '' },
                  { name: 'Northern Beaches', slug: '' },
                  { name: 'Inner West', slug: '' },
                  { name: 'Bayside', slug: '' },
                  { name: 'Randwick', slug: '' },
                  { name: 'Liverpool', slug: '' },
                ].map((c, i) => (
                  <Link key={i} to={c.slug ? `/council/${c.slug}` : '/waste-management-plan-nsw'} className="text-sm text-slate-600 hover:text-blue-600 transition-colors">{c.name}</Link>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-base font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded bg-blue-50 flex items-center justify-center text-blue-600 text-xs font-black">V</span>
                Victoria
              </h3>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                {['City of Melbourne', 'Darebin', 'Moreland/Merri-bek', 'Yarra', 'Port Phillip', 'Stonnington', 'Boroondara', 'Knox', 'Greater Geelong', 'Monash', 'Whitehorse', 'Glen Eira'].map((c, i) => (
                  <Link key={i} to={`/waste-management-plan-vic`} className="text-sm text-slate-600 hover:text-blue-600 transition-colors">{c}</Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0A2540]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4 tracking-tight">Need a Waste Management Plan?</h2>
          <p className="text-slate-400 text-base mb-8 max-w-lg mx-auto">Send us your plans and we&apos;ll get you a quote fast. Most Operational WMPs delivered within 5 business days.</p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/contact" className="px-8 py-3 rounded-full bg-white text-[#0A2540] font-semibold text-sm hover:bg-slate-100 transition-colors shadow-lg" data-testid="cta-quote">Request a Quote</Link>
            <a href="tel:0431469974" className="px-8 py-3 rounded-full border border-white/20 text-white font-medium text-sm hover:bg-white/10 transition-colors">Call Us</a>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
